import { styled, useTheme } from '@mui/material/styles'
import React from 'react'
//import { makeStyles } from 'tss-react/mui'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuItem from '@mui/material/MenuItem'
import Popper, { PopperProps } from '@mui/material/Popper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Checkbox, Divider, ListSubheader, useMediaQuery } from '@mui/material'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import { List, type ListImperativeAPI, type RowComponentProps } from 'react-window'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import { lodash } from 'src/utils'
import { ViewSlot } from 'wdc-cube-react'
import {
    JunctionMode,
    KeywordOption,
    MultipleOptionStringKeywordFilterScope,
    MultipleOptionStringKeywordParcelFilterScope,
    StringKeywordOperator,
    StringOperator
} from '../../tcm_scopes'
import { FieldIconButton, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('StringFilterView')

/* ================================
 * Constantes
 * ================================ */

const LISTBOX_PADDING = 8
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

/* ================================
 * Styled components
 * ================================ */

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0
        }
    },
    [`& .${autocompleteClasses.paper}`]: {
        border: '1px solid var(--border)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-2)'
    }
})

/* ================================
 * Popper Component (MUI 5 compatible)
 * ================================ */

const PopperComponent: React.ComponentType<PopperProps> = (props) => <StyledPopper {...props} />

/* ================================
 * react-window helpers
 * ================================ */
type OptionRow = [React.HTMLAttributes<HTMLLIElement> & { key: React.Key }, KeywordOption, boolean]

type ListboxItem =
    | OptionRow
    | {
          key: React.Key
          group: string
          children?: ListboxItem[]
      }

function RowComponent({ index, itemData, style }: RowComponentProps<{ itemData: ListboxItem[] }>) {
    const dataSet = itemData[index]

    const inlineStyle = {
        ...style,
        top: ((style.top as number) ?? 0) + LISTBOX_PADDING
    }

    if (!dataSet) return null

    if (!Array.isArray(dataSet)) {
        return (
            <ListSubheader component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        )
    }

    const { key, ...optionProps } = dataSet[0]

    return (
        <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} checked={dataSet[2]} sx={{ mr: 1 }} />
            {`${dataSet[1].label}`}
        </Typography>
    )
}

/* ================================
 * Virtualized Listbox
 * ================================ */
type ListboxComponentProps = React.HTMLAttributes<HTMLElement> & {
    internalListRef: React.Ref<ListImperativeAPI>
    onItemsBuilt: (optionIndexMap: Map<string, number>) => void
}

const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>(function ListboxComponent(props, ref) {
    const { children, internalListRef, onItemsBuilt, ...other } = props
    const itemData = [] as ListboxItem[]
    const optionIndexMap = React.useMemo(() => new Map<string, number>(), [])

    ;(children as ListboxItem[]).forEach((item) => {
        itemData.push(item)
        if (!Array.isArray(item) && Array.isArray(item.children)) {
            itemData.push(...item.children)
        }
    })

    // Posição de cada opção na lista achatada, usada para rolar até a opção destacada pelo teclado
    optionIndexMap.clear()
    itemData.forEach((item, index) => {
        if (Array.isArray(item)) {
            optionIndexMap.set(String(item[1].key), index)
        }
    })

    React.useEffect(() => {
        onItemsBuilt?.(optionIndexMap)
    }, [onItemsBuilt, optionIndexMap])

    const theme = useTheme()
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true })
    const itemSize = smUp ? 36 : 48
    const itemCount = itemData.length

    const getChildSize = (child: ListboxItem) => (Array.isArray(child) ? itemSize : 48)

    const height = itemCount > 8 ? 8 * itemSize : itemData.map(getChildSize).reduce((a, b) => a + b, 0)

    const { className, style: _style, ...otherProps } = other

    return (
        <div ref={ref} {...otherProps}>
            <List
                className={className}
                listRef={internalListRef}
                key={itemCount}
                rowCount={itemCount}
                rowHeight={(index) => getChildSize(itemData[index])}
                rowComponent={RowComponent}
                rowProps={{ itemData }}
                style={{ height: height + 2 * LISTBOX_PADDING, width: '100%' }}
                overscanCount={5}
                tagName="ul"
            />
        </div>
    )
})

type StringMultiSelectFilterViewProps = {
    className?: string
    scope: MultipleOptionStringKeywordFilterScope
}

class StringMultiSelectFilterViewClass implements FCClassContext<StringMultiSelectFilterViewProps> {
    // :: Fields
    scope!: MultipleOptionStringKeywordFilterScope

    onSyncState({ scope }: StringMultiSelectFilterViewProps) {
        this.scope = scope
    }

    render({ className }: StringMultiSelectFilterViewProps) {
        const { scope } = this

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <StringMultiParcelView
                                key={parcelScope.uid}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <StringMultiParcelView
                                key={parcelScope.uid}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    } //

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const StringMultiSelectFilterView = classToFComponent(StringMultiSelectFilterViewClass, React)
export default StringMultiSelectFilterView

// :: StringParcelViewClass

type StringParcelViewProps = {
    className?: string
    scope: MultipleOptionStringKeywordParcelFilterScope
    divider?: boolean
}

class StringMultiParcelViewClass implements FCClassContext<StringParcelViewProps> {
    scope!: MultipleOptionStringKeywordParcelFilterScope

    valueField = new (class {
        scope!: MultipleOptionStringKeywordParcelFilterScope
        elm?: HTMLDivElement | null
        focused = false

        readonly setRef = (el: HTMLDivElement) => {
            this.elm = el
        }

        readonly handleFocus = () => {
            this.focused = true
            this.scope.update()
        }

        readonly handleBlur = () => {
            this.focused = false
            this.scope.update()
        }

        readonly handleClearClick = () => {
            this.scope.onClear().catch(LOG.caught)
        }
    })()

    // API imperativa da lista virtualizada e índice das opções (react-window 2)
    readonly listRef: { current: ListImperativeAPI | null } = { current: null }
    optionIndexMap = new Map<string, number>()

    readonly handleItemsBuilt = (optionIndexMap: Map<string, number>) => {
        this.optionIndexMap = optionIndexMap
    }

    readonly handleHighlightChange = (_event: React.SyntheticEvent, option: KeywordOption | null) => {
        if (option && this.listRef.current) {
            const index = this.optionIndexMap.get(String(option.key))
            if (index !== undefined) {
                this.listRef.current.scrollToRow({ index, align: 'auto' })
            }
        }
    }

    filterOptions = (options: KeywordOption[], state: { inputValue: string }) => {
        const terms = extractNormalizedTerms(state.inputValue)
        if (!terms.length) {
            return options
        }

        return options.filter((option) => terms.some((term) => normalize(option.label).includes(term)))
    }

    render({ className, scope, divider }: StringParcelViewProps) {
        this.scope = scope
        this.valueField.scope = scope

        return (
            <>
                <FilterPanel className={className}>
                    {scope.extraPosition === 'begin' && lodash.isNotNil(scope.extra) && (
                        <ViewSlot scope={scope.extra} />
                    )}
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>
                            {criterionLabel(scope.junctionMode === JunctionMode.AND)}
                        </FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${StringOperator.contains}`}
                            value={`${scope.operator}`}
                            onChange={this.handleSelectChange}
                            sx={inputSx}
                            fullWidth
                        >
                            {scope.acceptBlankValues && (
                                <MenuItem value={StringKeywordOperator.is_blank}>Está em branco</MenuItem>
                            )}
                            {scope.acceptBlankValues && (
                                <MenuItem value={StringKeywordOperator.is_not_blank}>Não está em branco</MenuItem>
                            )}
                            <MenuItem value={StringKeywordOperator.is_in}>Está na lista</MenuItem>
                            <MenuItem value={StringKeywordOperator.is_not_in}>Não está na lista</MenuItem>
                        </TextField>
                    </Field>
                    {scope.valueVisible && (
                        <ClickAwayListener onClickAway={this.handleClickAway}>
                            <Field>
                                <FieldLabel htmlFor={`${scope.uid}-valores`}>Valores</FieldLabel>
                                <Autocomplete
                                    id={`${scope.uid}-valores`}
                                    multiple
                                    limitTags={2}
                                    filterOptions={this.filterOptions}
                                    disableCloseOnSelect
                                    disableListWrap
                                    options={scope.options}
                                    loading={scope.optionsLoading}
                                    loadingText="Carregando a lista…"
                                    noOptionsText={scope.optionsError || 'Nenhuma opção'}
                                    onOpen={this.handleOpen}
                                    getOptionLabel={(option: KeywordOption) => option.label}
                                    getOptionKey={(option: KeywordOption) => option.key}
                                    renderOption={(props, option, state) =>
                                        [props, option, state.selected] as React.ReactNode
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Selecione um ou mais valores"
                                            sx={inputSx}
                                            slotProps={{
                                                ...params.slotProps,

                                                input: {
                                                    ...params.slotProps.input,
                                                    endAdornment: (
                                                        <>
                                                            <FieldIconButton
                                                                size="small"
                                                                aria-label="Limpar valores"
                                                                onClick={this.handleClearClick}
                                                            >
                                                                <DeleteIcon />
                                                            </FieldIconButton>
                                                            {params.slotProps.input.endAdornment}
                                                        </>
                                                    )
                                                }
                                            }}
                                        />
                                    )}
                                    value={scope.getValueInOptionType()}
                                    onChange={(evt, value) => {
                                        this.handleOptionSelected(value)
                                    }}
                                    slots={{
                                        popper: PopperComponent
                                    }}
                                    onHighlightChange={this.handleHighlightChange}
                                    slotProps={{
                                        listbox: {
                                            component: ListboxComponent,
                                            internalListRef: this.listRef,
                                            onItemsBuilt: this.handleItemsBuilt
                                        } as object
                                    }}
                                />
                                <FieldHelp>
                                    Aceita colar vários códigos separados por vírgula ou ponto e vírgula.
                                </FieldHelp>
                            </Field>
                        </ClickAwayListener>
                    )}
                    {scope.extraPosition === 'end' && lodash.isNotNil(scope.extra) && <ViewSlot scope={scope.extra} />}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onOperatorChange(+event.target.value)
    }

    readonly handleOpen = () => {
        this.scope.onReloadOptions().catch(LOG.caught)
    }

    readonly handleClickAway = () => {
        this.scope.update()
    }

    readonly handleClearClick = () => {
        this.scope.onClear().catch(LOG.caught)
    }

    readonly handleOptionSelected = (options: KeywordOption[]): void => {
        this.scope.onOptionSelected(options).catch(LOG.caught)
    }
}

export const StringMultiParcelView = classToFComponent(StringMultiParcelViewClass, React)

const normalize = (str: string) =>
    str
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

const extractNormalizedTerms = (input: string): string[] => {
    if (!input.trim()) return []

    // Primeiro separa apenas por , e ;
    const commaSeparated = input.split(/[;,]+/)

    const finalTerms: string[] = []

    commaSeparated.forEach((chunk) => {
        const trimmed = chunk.trim()

        if (!trimmed) return

        // Se for apenas números e espaços → divide por espaço
        if (/^[\d\s]+$/.test(trimmed)) {
            trimmed
                .split(/\s+/)
                .filter(Boolean)
                .forEach((n) => finalTerms.push(n))
        } else {
            // Caso seja texto, mantém inteiro
            finalTerms.push(trimmed)
        }
    })

    return finalTerms.map(normalize)
}
